import { computed, inject, Injectable, signal } from '@angular/core';
import { info, warn } from '@4players/odin-common';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { HealthSample, StatsTarget, StreamStatsService } from './stream-stats.service';
import { LogThrottle } from '../../shared/helpers/log-throttle';

/**
 * Lifecycle state of one video stream, derived once per second from the
 * stats snapshot. The thresholds are deterministic, so a stream looks the
 * same on every client and a state only changes when its condition flips.
 */
export type StreamHealthState =
  /** Watched for less than the grace period, nothing received/produced yet. */
  | 'connecting'
  /** Grace period over and still no packet: the sender's data never arrives. */
  | 'no-media'
  /** Packets flow but no frame was ever decoded. */
  | 'waiting-keyframe'
  /** Packets stopped after the stream had been flowing. */
  | 'stalled'
  /** Packets keep flowing but decoding stopped producing frames. */
  | 'failed'
  /** Playing, but loss, freezes or an encoder limit exceed the thresholds. */
  | 'degraded'
  | 'healthy'
  /** Send side: the own capture/encoder never produced a frame. */
  | 'no-capture'
  /** Send side: the server never (or no longer) reports receiving us. */
  | 'no-ack'
  /** Send side, browser only: the stats carry no entry we can tie to our own
   *  stream (Firefox reports no track identity on outbound-rtp), so its health
   *  is unknown. Neutral: reading missing stats as a dead capture flagged
   *  working cameras as failed. */
  | 'untracked';

export type StreamHealthSeverity = 'ok' | 'pending' | 'warn' | 'error';

export type StreamKind = 'cam' | 'screen';

export interface StreamHealth {
  direction: 'send' | 'receive';
  state: StreamHealthState;
  severity: StreamHealthSeverity;
  /** ms in the current state. */
  sinceMs: number;
  /** ms this stream has been watched. */
  watchedForMs: number;
  native: boolean;
  announced: boolean;
  /** Times since these events (ms); null when they never happened. */
  firstPacketAgoMs: number | null;
  lastPacketAgoMs: number | null;
  firstFrameAgoMs: number | null;
  lastFrameAgoMs: number | null;
  packetsPerSec: number | null;
  fps: number | null;
  recentLossPct: number | null;
  freezes60s: number | null;
  keyframeRequests: number | null;
  droppedFrames: number | null;
  codec?: string;
  decoder?: string;
  encoder?: string;
  width?: number;
  height?: number;
  qualityLimitation?: string;
  /** Send side: ms since the server's last receiver report on our stream. */
  ackAgeMs: number | null;
  ackRttMs: number | null;
  /** Last raw sample, part of the copied diagnostics. */
  raw: HealthSample | null;
}

/** One watched stream with the health derived for it (null while unknown). */
export interface WatchedStream {
  key: string;
  userId: string;
  kind: StreamKind;
  health: StreamHealth | null;
}

/** No packet/frame movement for this long escalates the state. */
const GRACE_MS = 5_000;
/** Loss over the last ~5 s above this degrades the stream. */
const LOSS_WARN_PCT = 5;
/**
 * Freezes within the last 60 s at or above this degrade the stream. Native
 * stall recoveries only: the browser's freezeCount measures against the mean
 * frame interval, so a screen share sending few frames of a still picture
 * "freezes" all the time. It stays in the dialog as a figure.
 */
const FREEZES_WARN = 2;
/** Loss and a send-side quality limitation must hold this long to count. */
const DEGRADED_CONFIRM_MS = 5_000;
/**
 * The browser limits a fresh send stream while its bandwidth estimate ramps
 * up; only a limitation outlasting that counts.
 */
const LIMITED_CONFIRM_MS = 10_000;
/**
 * Loss over fewer packets is no rate: one lost packet of a quiet stream's 15
 * would read as 7 %. Quiet streams get a longer span instead (see `derive`).
 */
const LOSS_MIN_PACKETS = 100;
/** Send side: no receiver report this long after the first sent packet. */
const ACK_NEVER_MS = 8_000;
/** Send side: an established receiver report flow quiet for this long. */
const ACK_STALE_MS = 15_000;
/** Rolling sample window: loss and rates use its last ~5 s, freezes 60 s. */
const WINDOW_MS = 65_000;

interface WindowPoint {
  at: number;
  packets?: number;
  lost?: number;
  frames?: number;
  freezes?: number;
}

export interface StreamTracker {
  userId: string;
  kind: StreamKind;
  target: StatsTarget;
  /** Tiles currently showing this stream. */
  refs: number;
  watchedSince: number;
  firstPacketAt: number | null;
  lastPacketGrowthAt: number | null;
  firstFrameAt: number | null;
  lastFrameGrowthAt: number | null;
  prevCounter: number | null;
  prevFrames: number | null;
  window: WindowPoint[];
  state: StreamHealthState | null;
  stateSince: number;
  ackStamp: string | null;
  ackChangedAt: number | null;
  ackEverAt: number | null;
  /** Since when loss has been over the threshold; null while it isn't. */
  lossBadSince: number | null;
  /** Send side: since when the encoder has been limited; null while not. */
  limitedSince: number | null;
  /** Problem state last logged; only transitions are logged. */
  loggedProblem: StreamHealthState | null;
  loggedProblemAt: number;
}

export function streamKey(userId: string, kind: StreamKind): string {
  return `${userId}:${kind}`;
}

/** 2 for error, 1 for warn, 0 otherwise. */
export function severityRank(h: StreamHealth | null | undefined): number {
  if (!h) return 0;
  return h.severity === 'error' ? 2 : h.severity === 'warn' ? 1 : 0;
}

/** Short Title Case label of a state, translated by the caller. */
export function healthStateLabel(state: StreamHealthState | undefined): string {
  switch (state) {
    case 'no-media':
      return _('No Data From Sender');
    case 'stalled':
      return _('Stream Stalled');
    case 'failed':
      return _('Playback Failed');
    case 'waiting-keyframe':
      return _('Starting Video');
    case 'degraded':
      return _('Reduced Stream Quality');
    case 'no-capture':
      return _('Video Source Failed');
    case 'no-ack':
      return _('Not Reaching Server');
    case 'untracked':
      return _('No Statistics');
    case 'healthy':
      return _('Running');
    default:
      return _('Connecting');
  }
}

/**
 * Watches the video streams shown in tiles and derives a `StreamHealth` per
 * stream. Tiles register a stream while they render it; one shared tick
 * serves all of them off the deduplicated stats snapshot, and polling stops
 * once nothing is watched.
 */
@Injectable({ providedIn: 'root' })
export class StreamHealthService {
  private stats = inject(StreamStatsService);
  private trackers = new Map<string, StreamTracker>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private tickInFlight = false;
  private tickStartedAt = 0;
  // A lossy link can flap between degraded and healthy: at most one log
  // line per stream and state per minute.
  private logThrottle = new LogThrottle();

  private readonly _streams = signal<readonly WatchedStream[]>([]);
  /** Every watched stream, in watch order. */
  readonly streams = this._streams.asReadonly();
  /** Number of watched streams currently in a warn or error state. */
  readonly problemCount = computed(
    () => this._streams().filter((s) => severityRank(s.health) > 0).length
  );

  /**
   * Whether the last tick got any stats: null before the first tick, false
   * when the stats source answers nothing (no web stats source, native
   * session closed). The dialog says so instead of waiting forever.
   */
  readonly sourceOk = signal<boolean | null>(null);

  health(userId: string, kind: StreamKind): StreamHealth | null {
    return this._streams().find((s) => s.key === streamKey(userId, kind))?.health ?? null;
  }

  /**
   * Starts watching a stream for one tile. Every `watch` needs a matching
   * `unwatch`. A changed target is a different stream and restarts the
   * lifecycle.
   */
  watch(userId: string, kind: StreamKind, target: StatsTarget): void {
    const key = streamKey(userId, kind);
    const existing = this.trackers.get(key);
    if (existing && sameTarget(existing.target, target)) {
      existing.target = target;
      existing.refs += 1;
      return;
    }
    this.trackers.set(key, createTracker(userId, kind, target, (existing?.refs ?? 0) + 1));
    this.publish(key, null);
    this.ensureTimer();
  }

  unwatch(userId: string, kind: StreamKind): void {
    const key = streamKey(userId, kind);
    const tracker = this.trackers.get(key);
    if (!tracker) return;
    tracker.refs -= 1;
    if (tracker.refs > 0) return;
    this.trackers.delete(key);
    this._streams.update((list) => list.filter((s) => s.key !== key));
    if (this.trackers.size === 0 && this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Health of every watched stream including its raw sample, for reports. */
  diagnostics(): unknown[] {
    return this._streams().map(({ userId, kind, health }) => {
      const target = this.trackers.get(streamKey(userId, kind))?.target;
      return {
        userId,
        kind,
        target: target && { ...target, stream: undefined },
        health,
      };
    });
  }

  private publish(key: string, health: StreamHealth | null): void {
    const tracker = this.trackers.get(key);
    if (!tracker) return;
    const entry: WatchedStream = { key, userId: tracker.userId, kind: tracker.kind, health };
    this._streams.update((list) =>
      list.some((s) => s.key === key)
        ? list.map((s) => (s.key === key ? entry : s))
        : [...list, entry]
    );
  }

  private ensureTimer(): void {
    if (this.timer !== null) return;
    this.timer = setTimeout(() => void this.tick(), 0);
  }

  /**
   * While a stream starts up (nothing decoded yet, watched for less than
   * 15 s) the tick runs at 4 Hz, so the tile overlay vanishes right after
   * the first picture instead of lagging a 1 Hz tick behind.
   */
  private fastPhase(): boolean {
    const now = Date.now();
    for (const t of this.trackers.values()) {
      if (t.firstFrameAt === null && now - t.watchedSince < 15_000) return true;
    }
    return false;
  }

  private async tick(): Promise<void> {
    // A tick that never settled is abandoned after 10 s: the snapshot has its
    // own timeouts, this is the last line of defense.
    if (this.tickInFlight && Date.now() - this.tickStartedAt < 10_000) return;
    this.tickInFlight = true;
    this.tickStartedAt = Date.now();
    try {
      const maxAgeMs = this.fastPhase() ? 200 : 900;
      const results = new Map<string, StreamHealth | null>();
      let anySample = false;
      for (const [key, tracker] of this.trackers) {
        const sample = await this.stats.healthSample(tracker.target, maxAgeMs).catch(() => null);
        if (sample) anySample = true;
        const health = sample ? derive(tracker, sample) : null;
        if (health) this.logTransition(tracker, health);
        results.set(key, health);
      }
      // Trackers may have been removed or replaced while awaiting samples.
      this._streams.set(
        [...this.trackers.entries()].map(([key, t]) => ({
          key,
          userId: t.userId,
          kind: t.kind,
          health: results.get(key) ?? null,
        }))
      );
      if (this.trackers.size > 0) this.sourceOk.set(anySample);
    } finally {
      this.tickInFlight = false;
      this.timer =
        this.trackers.size > 0
          ? setTimeout(() => void this.tick(), this.fastPhase() ? 250 : 1_000)
          : null;
    }
  }

  /**
   * Logs when a stream enters a problem state and when it plays again. The
   * startup phase is no problem yet.
   */
  private logTransition(t: StreamTracker, h: StreamHealth): void {
    const problem = h.severity === 'warn' || h.severity === 'error' ? h.state : null;
    if (problem === t.loggedProblem) return;
    if (problem === null && h.state !== 'healthy') return;
    const label = `${t.target.direction} ${t.kind} ${t.userId}`;
    const now = Date.now();
    if (problem === null) {
      const lasted = Math.round((now - t.loggedProblemAt) / 1000);
      const previous = t.loggedProblem;
      t.loggedProblem = null;
      const repeat = this.logThrottle.hit(`${label}:healthy`);
      if (repeat !== null) {
        info(`[stream-health] ${label} recovered from ${previous} after ${lasted}s${repeat}`);
      }
      return;
    }
    t.loggedProblem = problem;
    t.loggedProblemAt = now;
    const repeat = this.logThrottle.hit(`${label}:${problem}`);
    if (repeat !== null) warn(`[stream-health] ${label} is ${problem}: ${healthFacts(h)}${repeat}`);
  }
}

export function createTracker(
  userId: string,
  kind: StreamKind,
  target: StatsTarget,
  refs: number,
  now = Date.now()
): StreamTracker {
  return {
    userId,
    kind,
    target,
    refs,
    watchedSince: now,
    firstPacketAt: null,
    lastPacketGrowthAt: null,
    firstFrameAt: null,
    lastFrameGrowthAt: null,
    prevCounter: null,
    prevFrames: null,
    window: [],
    state: null,
    stateSince: now,
    ackStamp: null,
    ackChangedAt: null,
    ackEverAt: null,
    lossBadSince: null,
    limitedSince: null,
    loggedProblem: null,
    loggedProblemAt: 0,
  };
}

/** Compact facts for a problem log line: what moved, what didn't. */
function healthFacts(h: StreamHealth): string {
  const ago = (ms: number | null) => (ms === null ? 'never' : `${Math.round(ms / 1000)}s ago`);
  const parts = [
    `watched ${Math.round(h.watchedForMs / 1000)}s`,
    `last packet ${ago(h.lastPacketAgoMs)}`,
    `last frame ${ago(h.lastFrameAgoMs)}`,
    `${h.packetsPerSec ?? '-'} pkt/s`,
    `${h.fps ?? '-'} fps`,
  ];
  if (h.recentLossPct !== null) parts.push(`loss ${h.recentLossPct}%`);
  if (h.freezes60s !== null) parts.push(`${h.freezes60s} freezes/60s`);
  if (h.direction === 'send') {
    parts.push(`ack ${ago(h.ackAgeMs)}`);
    if (h.qualityLimitation) parts.push(`limited by ${h.qualityLimitation}`);
  }
  const codec = [h.codec, h.encoder ?? h.decoder].filter(Boolean).join('/');
  if (codec) parts.push(codec);
  if (h.width && h.height) parts.push(`${h.width}x${h.height}`);
  if (!h.announced) parts.push('not announced');
  parts.push(h.native ? 'native' : 'web');
  return parts.join(', ');
}

function sameTarget(a: StatsTarget, b: StatsTarget): boolean {
  return a.direction === b.direction && a.mediaId === b.mediaId && a.trackId === b.trackId;
}

/** Advances the tracker with a sample and derives the stream's health. */
export function derive(t: StreamTracker, s: HealthSample, now = Date.now()): StreamHealth {
  const send = s.direction === 'send';

  // Flow detection on whichever cumulative counter the platform reports;
  // bytes stand in for missing packet counts.
  const counter = send ? (s.packetsSent ?? s.bytesSent) : (s.packetsReceived ?? s.bytesReceived);
  if (counter !== undefined) {
    // A counter already moving before we started watching counts as flow.
    if (t.prevCounter === null ? counter > 0 : counter > t.prevCounter) {
      t.lastPacketGrowthAt = now;
      t.firstPacketAt ??= now;
    }
    t.prevCounter = counter;
  }
  if (s.frames !== undefined) {
    if (t.prevFrames === null ? s.frames > 0 : s.frames > t.prevFrames) {
      t.lastFrameGrowthAt = now;
      t.firstFrameAt ??= now;
    }
    t.prevFrames = s.frames;
  }
  if (send && s.ack && s.ack.stamp !== t.ackStamp) {
    t.ackStamp = s.ack.stamp;
    t.ackChangedAt = now;
    t.ackEverAt ??= now;
  }

  // Rolling window for rates, recent loss and the freeze count.
  t.window.push({
    at: now,
    packets: send ? s.packetsSent : s.packetsReceived,
    lost: s.packetsLost,
    frames: s.frames,
    freezes: s.freezes,
  });
  while (t.window.length > 0 && now - t.window[0].at > WINDOW_MS) t.window.shift();
  // Rates compare against the newest point at least ~5 s old; the oldest one
  // would average over the whole minute and hide fresh trouble.
  let ref = t.window[0];
  for (let i = t.window.length - 1; i >= 0; i--) {
    if (now - t.window[i].at >= 4_500) {
      ref = t.window[i];
      break;
    }
  }
  const elapsed = (now - ref.at) / 1000;

  // Loss starts from the same ~5 s but reaches further back until the span
  // holds enough packets to be a rate: a quiet stream never fills 5 s, and
  // its loss would go unseen.
  let recentLossPct: number | null = null;
  let lossPackets = 0;
  if (s.packetsLost !== undefined && s.packetsReceived !== undefined) {
    for (let i = t.window.length - 1; i >= 0; i--) {
      const point = t.window[i];
      if (now - point.at < 4_500 && i > 0) continue;
      if (point.at >= now || point.lost === undefined || point.packets === undefined) continue;
      const lost = s.packetsLost - point.lost;
      const received = s.packetsReceived - point.packets;
      if (lost < 0 || received < 0 || lost + received === 0) continue;
      lossPackets = lost + received;
      recentLossPct = Math.round((lost / lossPackets) * 1000) / 10;
      if (lossPackets >= LOSS_MIN_PACKETS) break;
    }
  }
  const packetsNow = send ? s.packetsSent : s.packetsReceived;
  const packetsPerSec =
    elapsed > 0 &&
    ref.packets !== undefined &&
    packetsNow !== undefined &&
    packetsNow >= ref.packets
      ? Math.round((packetsNow - ref.packets) / elapsed)
      : null;
  const fps =
    elapsed > 0 && ref.frames !== undefined && s.frames !== undefined && s.frames >= ref.frames
      ? Math.round((s.frames - ref.frames) / elapsed)
      : null;
  let freezes60s: number | null = null;
  if (s.freezes !== undefined) {
    const first = t.window.find((w) => w.freezes !== undefined && now - w.at <= 60_000);
    if (first?.freezes !== undefined) freezes60s = Math.max(0, s.freezes - first.freezes);
  }

  // Send side: the receiver report's fraction lost is the honest number, and
  // a rate already, over the receiver's report interval.
  if (send) recentLossPct = s.ack?.lossPct ?? null;
  const lossBad =
    recentLossPct !== null &&
    recentLossPct > LOSS_WARN_PCT &&
    (send || lossPackets >= LOSS_MIN_PACKETS);
  t.lossBadSince = lossBad ? (t.lossBadSince ?? now) : null;
  const lossy = t.lossBadSince !== null && now - t.lossBadSince >= DEGRADED_CONFIRM_MS;
  const ackAgeMs = t.ackChangedAt === null ? null : now - t.ackChangedAt;
  const inGrace = now - t.watchedSince < GRACE_MS;
  const packetsStopped = t.lastPacketGrowthAt !== null && now - t.lastPacketGrowthAt > GRACE_MS;

  let state: StreamHealthState;
  if (send) {
    const limitedNow = s.qualityLimitation === 'bandwidth' || s.qualityLimitation === 'cpu';
    t.limitedSince = limitedNow ? (t.limitedSince ?? now) : null;
    const limited = t.limitedSince !== null && now - t.limitedSince >= LIMITED_CONFIRM_MS;
    if (!s.native && !s.announced) {
      state = inGrace ? 'connecting' : 'untracked';
    } else if (t.firstFrameAt === null) {
      state = inGrace ? 'connecting' : 'no-capture';
    } else if (t.firstPacketAt === null) {
      state = inGrace ? 'connecting' : 'no-ack';
    } else if (packetsStopped) {
      state = 'stalled';
    } else if (t.ackEverAt === null && now - t.firstPacketAt > ACK_NEVER_MS) {
      state = 'no-ack';
    } else if (ackAgeMs !== null && ackAgeMs > ACK_STALE_MS) {
      state = 'no-ack';
    } else if (lossy || limited) {
      state = 'degraded';
    } else {
      state = 'healthy';
    }
  } else if (t.firstPacketAt === null) {
    state = inGrace ? 'connecting' : 'no-media';
  } else if (t.firstFrameAt === null) {
    state = 'waiting-keyframe';
  } else if (packetsStopped) {
    state = 'stalled';
  } else if (t.lastFrameGrowthAt !== null && now - t.lastFrameGrowthAt > GRACE_MS) {
    state = 'failed';
  } else if (lossy || (s.native && freezes60s !== null && freezes60s >= FREEZES_WARN)) {
    state = 'degraded';
  } else {
    state = 'healthy';
  }
  if (state !== t.state) {
    t.state = state;
    t.stateSince = now;
  }

  let severity: StreamHealthSeverity;
  if (state === 'healthy' || state === 'untracked') severity = 'ok';
  else if (state === 'degraded') severity = 'warn';
  else if (state === 'connecting') severity = 'pending';
  else if (state === 'waiting-keyframe')
    // Packets without a picture for longer than the grace period.
    severity = t.firstPacketAt !== null && now - t.firstPacketAt > GRACE_MS ? 'warn' : 'pending';
  else severity = 'error';

  return {
    direction: s.direction,
    state,
    severity,
    sinceMs: now - t.stateSince,
    watchedForMs: now - t.watchedSince,
    native: s.native,
    announced: s.announced,
    firstPacketAgoMs: t.firstPacketAt === null ? null : now - t.firstPacketAt,
    lastPacketAgoMs: t.lastPacketGrowthAt === null ? null : now - t.lastPacketGrowthAt,
    firstFrameAgoMs: t.firstFrameAt === null ? null : now - t.firstFrameAt,
    lastFrameAgoMs: t.lastFrameGrowthAt === null ? null : now - t.lastFrameGrowthAt,
    packetsPerSec,
    fps,
    recentLossPct,
    freezes60s: send ? null : freezes60s,
    keyframeRequests: send ? null : (s.pliCount ?? null),
    droppedFrames: send ? null : (s.dropped ?? null),
    codec: s.codec,
    decoder: s.decoder,
    encoder: s.encoder,
    width: s.width,
    height: s.height,
    qualityLimitation: s.qualityLimitation,
    ackAgeMs: send ? ackAgeMs : null,
    ackRttMs: send ? (s.ack?.rttMs ?? null) : null,
    raw: s,
  };
}
